// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Megaphone, Plus } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Select, Pagination, DataTable, Skeleton } from '@/components/ui'
import type { Column } from '@/components/ui/DataTable'
import { useAnnouncements, type AnnouncementFilters } from '@/hooks/useAnnouncements'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { Announcement, AnnouncementStatus } from '@/types'

export function AnnouncementListPage() {
  const { t } = useTranslation('announcements')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const formatDate = useDateFormatter()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<AnnouncementFilters>({})
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  const { data, isLoading, isFetching, error } = useAnnouncements(page, filters)
  const announcements = data?.data || []

  const statusOptions = [
    { value: '', label: tc('labels.all') },
    { value: 'active', label: t('status.active') },
    { value: 'scheduled', label: t('status.scheduled') },
    { value: 'expired', label: t('status.expired') },
  ]

  const targetTypeOptions = [
    { value: '', label: tc('labels.all') },
    { value: 'all', label: t('target.all') },
    { value: 'property', label: t('target.property') },
    { value: 'unit', label: t('target.unit') },
  ]

  const getStatusBadge = (announcement: Announcement) => {
    const variants: Record<AnnouncementStatus, 'success' | 'warning' | 'ghost'> = {
      active: 'success',
      scheduled: 'warning',
      expired: 'ghost',
    }
    return (
      <Badge variant={variants[announcement.status]}>
        {t(`status.${announcement.status}`)}
      </Badge>
    )
  }

  const getTargetLabel = (announcement: Announcement) => {
    if (announcement.target_type === 'all') return t('target.all')
    return announcement.target_label || '-'
  }

  const columns: Column<Announcement>[] = [
    {
      key: 'title',
      header: t('form.title_label'),
      render: (a) => (
        <div>
          <span className="font-medium">{a.title}</span>
          {a.body && (
            <p className="text-sm text-base-content/60 truncate max-w-xs">{a.body}</p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: tc('labels.status'),
      render: (a) => getStatusBadge(a),
    },
    {
      key: 'target_type',
      header: t('form.target_label'),
      render: (a) => getTargetLabel(a),
    },
    {
      key: 'published_at',
      header: t('detail.published'),
      render: (a) => formatDate(a.published_at),
      hideBelow: 'md',
    },
  ]

  const activeAnnouncementFilterCount = [filters.status || '', filters.target_type || ''].filter(Boolean).length

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('title') }]} />

      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        isRefreshing={isFetching && !isLoading}
        actions={
          <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/announcements/new')}>
            {t('new_button')}
          </Button>
        }
      />

      <Card>
        {/* Mobile-only toggle */}
        <button
          type="button"
          className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium"
          aria-expanded={mobileFiltersOpen}
          aria-controls="announcement-filters-body"
          onClick={() => setMobileFiltersOpen((v) => !v)}
        >
          <span>
            {tc('filters.toggle')}
            {activeAnnouncementFilterCount > 0 && (
              <Badge variant="count" size="sm" className="ml-2">{activeAnnouncementFilterCount}</Badge>
            )}
          </span>
          <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
        </button>

        <div
          id="announcement-filters-body"
          className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
        >
          <Select
            label={tc('labels.status')}
            value={filters.status || ''}
            onChange={(e) => {
              setFilters((f) => ({ ...f, status: e.target.value || undefined }))
              setPage(1)
            }}
            options={statusOptions}
            className="w-full sm:w-48 min-w-0"
          />
          <Select
            label={t('form.target_label')}
            value={filters.target_type || ''}
            onChange={(e) => {
              setFilters((f) => ({ ...f, target_type: e.target.value || undefined }))
              setPage(1)
            }}
            options={targetTypeOptions}
            className="w-full sm:w-48 min-w-0"
          />
        </div>
      </Card>

      {isLoading ? (
        <Card>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            title={tc('errors.something_went_wrong')}
            action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
          />
        </Card>
      ) : !announcements.length ? (
        <Card>
          <EmptyState
            icon={<Megaphone className="w-12 h-12" />}
            title={t('empty.title')}
            description={t('empty.description')}
            action={
              <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/announcements/new')}>
                {t('new_button')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            data={announcements}
            keyExtractor={(a) => a.id}
            onRowClick={(a) => navigate(`/announcements/${a.id}`)}
          />
        </Card>
      )}

      {data?.meta && data.meta.total_count > 0 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={data.meta.current_page}
            totalPages={data.meta.total_pages}
            totalItems={data.meta.total_count}
            pageSize={data.meta.per_page}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  )
}
